/**
 * 创作指南卡片 - 在 Step1 顶部显示，帮助新用户快速了解创作流程
 * - AI 模式 vs DIY 模式显示不同的说明内容
 * - 可关闭，关闭状态保存到 localStorage
 * - 可展开/收起详细说明
 */
import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { BookOpen, X, ChevronDown, ChevronUp, Lightbulb, AlertCircle, CheckCircle2 } from 'lucide-react';

interface GuideCardProps {
  flow: 'ai' | 'diy';
}

const GUIDE_STORAGE_KEY = 'badibook_guide_dismissed';

export default function GuideCard({ flow }: GuideCardProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    const dismissed = localStorage.getItem(GUIDE_STORAGE_KEY);
    if (!dismissed) {
      setIsVisible(true);
    }
  }, []);

  const handleDismiss = () => {
    setIsVisible(false);
    localStorage.setItem(GUIDE_STORAGE_KEY, 'true');
  };

  const handleShowAgain = () => {
    setIsVisible(true);
    localStorage.removeItem(GUIDE_STORAGE_KEY);
  };

  const isAI = flow === 'ai';

  const quickTips = isAI
    ? [
        { icon: CheckCircle2, text: '上传孩子照片 → AI生成专属角色形象' },
        { icon: CheckCircle2, text: '填写故事主题和大纲 → AI自动创作完整故事' },
        { icon: CheckCircle2, text: '选择视觉风格 → AI生成每页精美插图' },
        { icon: CheckCircle2, text: '确认文案后即可生成语音 + PDF下载' },
      ]
    : [
        { icon: CheckCircle2, text: '设定角色（可不上传照片，用文字描述即可）' },
        { icon: CheckCircle2, text: '进入编辑器 → 逐页手写故事内容' },
        { icon: CheckCircle2, text: '为每页编写插图描述 → AI按描述生成画面' },
        { icon: CheckCircle2, text: '完成后生成语音 + PDF，支持跟读录音' },
      ];

  const detailedSteps = isAI
    ? [
        {
          title: '第一步：角色设定',
          content: [
            '选择「人物」或「动物」作为主角类型。',
            '如选动物，需进一步选择物种（兔子、小熊、恐龙等），并选择公/母。',
            '上传孩子照片（AI模式建议上传，用于生成角色一致性插图）。',
            '填写角色名称、性格特点，AI会据此塑造角色行为。',
            '外观描述可手动填写，或上传照片后点击「提取外观」自动生成。',
            '可选：添加 1-3 个配角（人物或动物），AI会在故事中安排它们出场。',
          ],
        },
        {
          title: '第二步：故事蓝图',
          content: [
            '选择目标年龄（3-4岁 / 5-6岁 / 7-8岁），影响词汇难度和句式复杂度。',
            '填写故事主题（如：勇敢面对恐惧、友谊、探索自然）。',
            '提供故事大纲或关键词（越详细，AI生成的故事越贴合需求）。',
            '选择故事页数（建议 8-12 页）。',
          ],
        },
        {
          title: '第三步：视觉风格',
          content: [
            '选择插图风格：Pixar 3D、Disney 3D、水彩绘本、铅笔素描。',
            '选择朗读音色和语速（可预览试听）。',
            '点击「开始创作」后，AI 将依次生成：故事文案 → 插图 → 语音。',
            '生成完成后可逐页预览，对不满意的页面可重新生成。',
          ],
        },
        {
          title: '动物故事特别提醒',
          content: [
            '主角类型选「动物」后，物种选择和公/母性别会展开。',
            '配角支持人物和动物两种类型，可添加人类朋友或动物伙伴。',
            'AI 会自动将预设的配角融入故事，保持外貌和名称一致。',
            '选择物种后，外观描述会显示该物种的典型特征提示，帮助您填写。',
            '不上传照片时，请尽可能详细地描述动物外貌（毛色、体型、标志性特征）。',
          ],
        },
      ]
    : [
        {
          title: '第一步：角色设定',
          content: [
            '选择「人物」或「动物」作为主角类型。',
            '如选动物，需选择物种并描述外貌特征。',
            '照片为可选：可以只通过文字描述角色形象，AI 会根据描述生成插图。',
            '填写角色名称、性格特点，这些会作为插图生成的参考。',
            '可添加 1-3 个配角，在后续编辑器中指定它们出现在哪些页面。',
          ],
        },
        {
          title: '第二步：故事蓝图',
          content: [
            '配置目标年龄、故事页数。',
            '填写主题和故事方向（帮助你自己构思故事主线，AI 不会在此代写）。',
            '可选择是否提供故事灵感提示，方便后续手写时参考。',
          ],
        },
        {
          title: '第三步：亲子编辑器',
          content: [
            '进入 DIY 编辑器后，左侧是页面导航，右侧是编辑区。',
            '每页包含：故事文字 + 插图描述 + 出场角色勾选。',
            '「插图描述」是关键：写得越详细，AI 生成的画面越符合预期。',
            '出场角色勾选：决定当前页面显示哪些配角（未勾选则不会出现在插图中）。',
            '所有内容自动保存，可随时返回修改。',
          ],
        },
        {
          title: '完成与导出',
          content: [
            '所有页面填写完成后，进入 Stage 2 生成语音。',
            'Stage 3 根据每页的「插图描述」生成对应画面。',
            '支持下载 PDF、在线阅读、跟读录音。',
          ],
        },
      ];

  const notices = isAI
    ? [
        '照片质量直接影响角色一致性：建议使用正面、光线均匀、背景简洁的照片。',
        '外观描述中的颜色词汇会被标准化（如「淡黄」→「light yellow」），确保插图颜色准确。',
        '配角最多 3 个，名称和外貌设定后 AI 会严格遵循，不要随意更改已生成故事里的配角名。',
        '故事生成后可在 Stage 1 页面逐页编辑文字，修改后保存会重新提取配角出场页码。',
      ]
    : [
        'DIY 模式下照片完全可选，不上传照片时请务必填写详细的外貌描述。',
        '「插图描述」是决定画面质量的核心：写明场景、角色动作、背景细节、情绪氛围。',
        '配角需要在每页的「出场角色」中手动勾选，未勾选则不会出现在该页插图中。',
        '编辑过程中内容每 3 秒自动保存，但建议完成一页后手动检查是否保存成功。',
      ];

  if (!isVisible) {
    return (
      <button
        onClick={handleShowAgain}
        className="mb-4 flex items-center gap-1.5 text-xs text-indigo-500 hover:text-indigo-600 transition-colors self-start"
      >
        <BookOpen className="w-3.5 h-3.5" />
        显示创作指南
      </button>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      className="mb-6 bg-gradient-to-r from-indigo-50/80 to-blue-50/80 border border-indigo-100 rounded-2xl overflow-hidden"
    >
      {/* 头部 */}
      <div className="flex items-center justify-between px-5 py-3.5 border-b border-indigo-100/60">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-indigo-100 flex items-center justify-center">
            <BookOpen className="w-4 h-4 text-indigo-600" />
          </div>
          <h3 className="text-sm font-semibold text-indigo-900">
            {isAI ? 'AI 全自动创作指南' : '亲子自制绘本指南'}
          </h3>
          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-indigo-100 text-indigo-600 font-medium">
            {isAI ? 'AI 模式' : 'DIY 模式'}
          </span>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="p-1.5 rounded-lg hover:bg-indigo-100/50 text-indigo-500 transition-colors"
            title={isExpanded ? '收起' : '展开'}
          >
            {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
          <button
            onClick={handleDismiss}
            className="p-1.5 rounded-lg hover:bg-rose-100/50 text-slate-400 hover:text-rose-500 transition-colors"
            title="关闭（可在页面底部重新打开）"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* 快速流程 */}
      <div className="px-5 py-3">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {quickTips.map((tip, idx) => (
            <div key={idx} className="flex items-start gap-2">
              <tip.icon className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
              <span className="text-xs text-slate-600 leading-relaxed">{tip.text}</span>
            </div>
          ))}
        </div>
      </div>

      {/* 详细说明（可展开） */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="overflow-hidden"
          >
            <div className="px-5 pb-4 space-y-4">
              {/* 分步详解 */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {detailedSteps.map((section, idx) => (
                  <div
                    key={idx}
                    className="bg-white/60 rounded-xl border border-indigo-50 p-3.5"
                  >
                    <h4 className="text-xs font-semibold text-indigo-800 mb-2 flex items-center gap-1.5">
                      <span className="w-5 h-5 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center text-[10px] font-bold">
                        {idx + 1}
                      </span>
                      {section.title}
                    </h4>
                    <ul className="space-y-1.5">
                      {section.content.map((item, i) => (
                        <li key={i} className="text-[11px] text-slate-600 leading-relaxed flex items-start gap-1.5">
                          <span className="w-1 h-1 rounded-full bg-indigo-300 mt-1.5 flex-shrink-0" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>

              {/* 注意事项 */}
              <div className="bg-amber-50/60 border border-amber-100 rounded-xl p-3.5">
                <h4 className="text-xs font-semibold text-amber-800 mb-2 flex items-center gap-1.5">
                  <AlertCircle className="w-3.5 h-3.5" />
                  注意事项
                </h4>
                <ul className="space-y-1.5">
                  {notices.map((item, i) => (
                    <li key={i} className="text-[11px] text-slate-600 leading-relaxed flex items-start gap-1.5">
                      <Lightbulb className="w-3 h-3 text-amber-500 mt-0.5 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* 底部提示 */}
      {!isExpanded && (
        <div className="px-5 pb-3">
          <button
            onClick={() => setIsExpanded(true)}
            className="text-xs text-indigo-500 hover:text-indigo-600 font-medium flex items-center gap-1 transition-colors"
          >
            <ChevronDown className="w-3.5 h-3.5" />
            查看详细步骤与注意事项
          </button>
        </div>
      )}
    </motion.div>
  );
}
